import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Checkbox, Col, Row, Space } from 'antd'
import { PrimaryButton } from 'Components/button'
import { isFormRequired, isFormError, formValidation } from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import { AppInput } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { createStaffAPI, staffDetailAPI, updateStaffAPI } from 'Store/pos/api'
import {
  CreateStaffReq,
  MerchantPackageList,
  PackageData,
  RoleData,
  StaffDetailReq,
  UpdateStaffReq,
} from 'Store/pos/types'
import { selectActionEvent, selectDetailEvent, selectDetail } from 'Store/pos/selector'
import { clearEventAction } from 'Store/pos/actions'
import Theme, { MyTheme } from 'Components/common/Theme'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
  roles: PackageData[]
  activePackage?: MerchantPackageList
}

const UserDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
    roles = [],
    activePackage,
  } = props

  const isCreateMode = mode === 'create'
  const requireFeild = ['name']
  const initForm = {
    name: '',
    pin: '',
    package_roles: [],
  } as CreateStaffReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    if (isCreateMode) {
      if (formData?.pin && formData?.pin.toString().length !== 4) {
        error.push('pin')
      }
    }
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateStaffReq = {
      ...formData,
      pin: formData?.pin && formData?.pin.toString(),
    }
    dispatch(createStaffAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  const onGetDetail = (): void => {
    const req: StaffDetailReq = {
      id,
    }
    dispatch(staffDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateStaffReq = {
      ...formData,
      id,
    }
    dispatch(updateStaffAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  useEffect(() => {
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }

    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    }
  }, [visible])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const roles = [] as any
      if (detail && detail.package_roles) {
        detail.package_roles.forEach(item => {
          roles.push({
            id: item.id,
            roles: item.role_ids || [],
          })
        })
      }
      const form = {
        name: detail?.name || '',
        package_roles: roles,
        pin: '',
      } as CreateStaffReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const hasConfig = (packageID: number) => {
    const has = formData.package_roles.find(item => item.id === packageID)
    return has ? true : false
  }

  const onUpdateRole = (val: number[], packageID: number) => {
    let roles = cloneDeep(formData.package_roles)
    if (hasConfig(packageID)) {
      roles = roles.map(item => {
        if (item.id === packageID) {
          item.roles = val
        }
        return item
      })
    } else {
      roles.push({
        id: packageID,
        roles: val,
      })
    }

    setFormData({
      ...formData,
      package_roles: roles,
    })
  }

  const onUpdateData = (val: any, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const onUpdatePin = (val: string) => {
    if (val.toString().length > 4) return
    setFormData({
      ...formData,
      pin: val,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const title = isCreateMode ? 'สร้างผู้ใช้งาน' : 'แก้ไขผู้ใช้งาน'

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={FooterContent}
      width={'30%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={[16, 8]}>
        <Col lg={24} xs={24}>
          <AppInput
            name="name"
            label="ชื่อผู้ใช้งาน"
            isRequired={isRequired('name')}
            isError={isError('name')}
            value={formData.name}
            onChange={onUpdateData}
          />
        </Col>
        {isCreateMode && (
          <Col lg={24} xs={24}>
            <AppInput
              name="pin"
              label="รหัส PIN ผู้ใช้งาน (4 หลัก)"
              isRequired={true}
              isError={isError('pin')}
              value={formData.pin}
              onChange={onUpdatePin}
              type="number"
            />
          </Col>
        )}
        <Col lg={24} xs={24}>
          <BoxTitle>จัดการสิทธิ์การใช้งาน</BoxTitle>
        </Col>
        {roles.map((item: PackageData, index: number) => {
          const value = formData.package_roles.find(form => form.id === item.id)
          const isActive = item.id === activePackage?.id
          return (
            <Col lg={24} xs={24} key={index}>
              <BoxWrapper style={{ borderColor: isActive ? Theme.colors.primary : '#f0f0f0' }}>
                <BoxTitle>
                  <Row justify="space-between">
                    <Col>{item.name}</Col>
                    {isActive && formData.package_roles.length > 1 && (
                      <Col>
                        <Remark>เปิดใช้งาน</Remark>
                      </Col>
                    )}
                  </Row>
                </BoxTitle>

                <Checkbox.Group
                  disabled={false}
                  value={value?.roles || []}
                  onChange={(val: any) => onUpdateRole(val, item.id)}
                >
                  <Row>
                    {item.roles.map((item: RoleData, index: number) => {
                      return (
                        <Col span={24} key={index}>
                          <Checkbox value={item.id}>{item.name}</Checkbox>
                        </Col>
                      )
                    })}
                  </Row>
                </Checkbox.Group>
              </BoxWrapper>
            </Col>
          )
        })}

        {/* <Col lg={24} xs={24}>
          <Switch checked={isPublic} onChange={handleToggleStatus} />
          &nbsp;
          <LabelItem label={`สถานะการเข้าใข้งาน : ${getUserStatus(formData.status)}`} />
        </Col> */}
      </Row>
    </DetailDrawer>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-bottom: 10px;
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 10px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const Remark = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectActionEvent(state),
    detail: selectDetail(state),
    detailEvent: selectDetailEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(UserDetail)
